import { observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import ArrowDownIcon from "@mui/icons-material/ArrowDownward";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import { DataGrid, GridFilterModel, GridCellParams, GridRowModel } from "@mui/x-data-grid";
import { jaJP } from "@mui/x-data-grid/locales";
import MuiCircularProgress from "@mui/material/CircularProgress";
// Source
import { lang } from "../../lang";
import { DocumentBody, MenuItem, Workbook, CellChange, TableColumn, WorkbookParams } from "../../interface";
import { store } from "../../store";
import {} from "../../interface";
import { ALERT_TYPE } from "../../HelperSrc";
import { getTableData } from "../../actions/getTableData";
import { getWorkbook } from "../../actions/getWorkbook";
import { tempLockWorkbook } from "../../actions/lockWorkbook";
import { updateTableData } from "../../actions/updateTableData";
import { columnLookup } from "../../data/columnLookup";
import { outputTables } from "../../data/outputTables";
import { Button } from "../../components/atoms/Button";
import { FlexHorizontal, FlexSpacer } from "../../components/atoms/Flex";
import { Title } from "../../components/atoms/Text";
import { Dropdown } from "../../components/molecules/Dropdown";
import { Template } from "../../components/organisms/Template";
import { getDownloadList } from "../../actions/getDownloadList";
import { catchEndpoint } from "../../catchEndpoint";

@observer
class WorkbookPageBase extends React.Component<RouteComponentProps<WorkbookParams>> {
    public refreshTimer: NodeJS.Timeout | null = null;
    public gridRef = React.createRef<HTMLDivElement>();
    public state = {
        changes: [] as CellChange[],
        rows: null as unknown as Workbook[],
        workbook: null as unknown as Workbook,
        loading: true,
        filters: { items: [] } as GridFilterModel,
        setFilters: false as boolean,
        editSaving: false
    };

    constructor(props: RouteComponentProps<WorkbookParams>) {
        super(props);
        store.setCurrentTable(outputTables[0]);
    }

    public async componentDidMount() {
        const { match } = this.props;
        const { workbookid } = match.params;
        const workbook = await getWorkbook(Number(workbookid));
        const rows = await getTableData(workbookid, store.currentTable.key);
        this.setState({
            rows,
            workbook,
            loading: false,
            filters: {
                items: [
                    {
                        id: "entity_code",
                        field: "entity_code",
                        operator: "contains",
                        value: ""
                    }
                ]
            }
        });
        this.refreshTimer = setInterval(this.updateEdit, 4000);
        (document.body as DocumentBody).onunload = () => {
            if (store.editing) {
                this.onCancelEdit();
            }
        };
    }

    public componentWillUnmount() {
        if (this.refreshTimer) clearInterval(this.refreshTimer);
        this.refreshTimer = null;
        store.setEditing(false);
        (document.body as DocumentBody).onunload = undefined;
    }

    public updateEdit = () => {
        const { match } = this.props;
        const { workbookid } = match.params;
        if (!store.editing) {
            getWorkbook(Number(workbookid))
                .then((workbook) => {
                    if (this.refreshTimer !== null) {
                        this.setState({ workbook });
                    }
                })
                .catch(catchEndpoint);
        } else {
            tempLockWorkbook(workbookid, true)
                .then(async (lockOk) => {
                    if (!lockOk && store.editing) {
                        store.setAlertData({
                            type: ALERT_TYPE.ALERT,
                            message: lang.pleaseWait
                        });
                        store.setEditing(false);
                        const workbook = this.state.workbook;
                        workbook.edit_locked = true;

                        this.setState({ workbook: workbook, changes: [] });
                        return getTableData(workbookid, store.currentTable.key);
                    }
                })
                .then((newRows) => {
                    if (newRows) {
                        this.setState({ rows: newRows });
                    }
                })
                .catch(catchEndpoint);
        }
    };

    public processRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
        const { changes } = this.state;

        const changedFields = Object.keys(newRow).filter((key) => newRow[key] !== oldRow[key]);

        if (changedFields.length === 0) {
            return oldRow;
        }

        const field = changedFields[0];
        let value = newRow[field] ? String(newRow[field]) : "";
        let v = value;

        const columnData = columnLookup[store.currentTable.key].find((c) => c.key === field);
        if (columnData && columnData.type === "date") {
            let pts = value.split("-");
            if (pts.length === 3) {
                pts = pts.map((p) => (p.length < 2 ? "0" + p : p));
            }
            v = value = pts.join("");
            if (
                !String(v).match(
                    new RegExp(
                        [
                            // Year Regex
                            "^(2[0-9]{3})",
                            // Months with 31 days
                            "(((01|03|05|07|08|10|12)",
                            "(0[1-9]|[1-2][0-9]|30|31))|",
                            // Months with 30 days
                            "((04|06|09|11)",
                            "(0[1-9]|[1-2][0-9]|30))|",
                            // Months with 29 or less days
                            "(02",
                            "(0[1-9]|[1-2][0-9])))$"
                        ].join("")
                    )
                ) &&
                value.length > 0
            ) {
                store.setAlertData({
                    type: ALERT_TYPE.ERROR,
                    message: lang.invalidDate
                });
                return oldRow;
            }
        }
        if (columnData && columnData.type !== "date") {
            const pts = value.split(",");
            v = value = pts.join("");
            if (isNaN(Number(v))) {
                store.setAlertData({
                    type: ALERT_TYPE.ERROR,
                    message: lang.invalidValue
                });
                return oldRow;
            }
        }

        const updatedData: CellChange = {
            row: newRow._id || "",
            name: field,
            value: value,
            original: oldRow[field] === null ? "" : String(oldRow[field])
        };
        if (updatedData.value === updatedData.original) {
            return newRow;
        }
        const existing = changes.find((chng: CellChange) => {
            return chng.row === updatedData.row && chng.name === updatedData.name;
        });
        if (existing) {
            existing.value = updatedData.value;
            if (String(existing.value) === existing.original) {
                changes.splice(changes.indexOf(existing), 1);
            }
        } else {
            changes.push(updatedData);
        }
        this.setState({ changes });

        return newRow;
    };

    public onSaveEdit = () => {
        const { match } = this.props;
        const { workbookid } = match.params;
        if (store.editing) {
            this.setState({ editSaving: true });
            updateTableData(workbookid, store.currentTable.key, this.state.changes)
                .then(() => {
                    this.setState({ changes: [] });
                    return getTableData(workbookid, store.currentTable.key);
                })
                .then((newRows) => {
                    this.setState({ rows: newRows });
                    this.setState({ editSaving: false });
                    store.setEditing(false);
                })
                .catch(catchEndpoint);
        } else {
            // Get Lock
            if (this.state.workbook.edit_locked) {
                store.setAlertData({
                    type: ALERT_TYPE.ALERT,
                    message: lang.pleaseWait
                });
                return;
            }
            this.setState({ editSaving: true });
            tempLockWorkbook(workbookid)
                .then(async (lockOk) => {
                    if (!lockOk) {
                        store.setAlertData({
                            type: ALERT_TYPE.ALERT,
                            message: lang.pleaseWait
                        });
                    } else {
                        const newRows = await getTableData(workbookid, store.currentTable.key);
                        this.setState({ rows: newRows });
                        store.setEditing(true);
                    }
                    this.setState({ editSaving: false });
                })
                .catch(catchEndpoint);
        }
    };
    public onCancelEdit = () => {
        const { match } = this.props;
        const { workbookid } = match.params;
        if (store.editing) {
            store.setEditing(false);
            updateTableData(workbookid, store.currentTable.key, [])
                .then(() => {
                    this.setState({ changes: [] });
                    return getTableData(workbookid, store.currentTable.key);
                })
                .then((newRows) => {
                    this.setState({ rows: newRows });
                })
                .catch(catchEndpoint);
        }
    };

    public formatValue = (params: GridCellParams) => {
        if (params && params.formattedValue) {
            return params.formattedValue;
        }

        return "";
    };

    public renderCell = (params: GridCellParams) => {
        const { changes } = this.state;
        const changeId = changes.findIndex((e) => {
            return params.field === e.name && e.row === params.row._id;
        });

        const wasChanged = changeId !== -1;
        const v = params.value;
        let dispV = v;
        if (v === "" && wasChanged) {
            dispV = changes[changeId].original;
        }

        let extraStyle = {};
        const columnData = columnLookup[store.currentTable.key].find((c) => c.key === params.field);
        if (columnData?.type === "date" && dispV) {
            let strDispV = String(dispV);
            dispV = `${strDispV.slice(0, 4)}-${strDispV.slice(4, 6)}-${strDispV.slice(6, 8)}`;
        } else if (dispV && dispV !== null && dispV !== "" && !isNaN(Number(dispV))) {
            extraStyle = { textAlign: "right" };
            // Regex removes decimal places in IE.
            const numDispV = Number(dispV)
                .toLocaleString("us", {
                    useGrouping: true
                })
                .match(/[0-9,-]*/);
            if (numDispV) dispV = numDispV[0];
        }

        let editable = store.editing ? columnData?.editable : false;

        if (store.currentTable.key == "table114" && params.field == "tax_item" && "row_number" in params.row) {
            const { row_number } = params.row;
            if ([3, 4, 10].includes(row_number as number)) {
                editable = editable && true;
            } else {
                editable = false;
            }
        }
        if (!editable) {
            return (
                <div
                    style={{
                        position: "absolute",
                        top: "0px",
                        left: "0px",
                        width: "100%",
                        height: "100%",
                        boxSizing: "border-box",
                        padding: "5px 8px 5px 8px",
                        background: "rgb(248,248,248)",
                        color: "gray",
                        ...extraStyle
                    }}
                    data-at_id={`column_${params.field}_${params.row.entity_code}_${params.row.row_number}`}
                >
                    {dispV}
                </div>
            );
        }
        if (wasChanged) {
            if (v === "") {
                return (
                    <div
                        style={{
                            position: "absolute",
                            top: "0px",
                            left: "0px",
                            width: "100%",
                            height: "100%",
                            boxSizing: "border-box",
                            padding: "5px 8px 5px 8px",
                            color: "rgb(224,224,224)",
                            textDecoration: "line-through",
                            ...extraStyle
                        }}
                        data-at_id={`column_${params.field}_${params.row.entity_code}_${params.row.row_number}`}
                    >
                        {dispV}
                    </div>
                );
            } else {
                return (
                    <div
                        style={{
                            fontWeight: "bold",
                            ...extraStyle
                        }}
                        data-at_id={`column_${params.field}_${params.row.entity_code}_${params.row.row_number}`}
                    >
                        {dispV}
                    </div>
                );
            }
        }
        return (
            <div
                style={{
                    ...extraStyle
                }}
                data-at_id={`column_${params.field}_${params.row.entity_code}_${params.row.row_number}`}
            >
                {dispV}
            </div>
        );
    };

    public handleSelectWorkbook = (tbl: MenuItem) => {
        const { match } = this.props;
        const { workbookid } = match.params;
        store.setCurrentTable(tbl);
        this.setState({
            rows: null,
            loading: true,
            setFilters: false
        });
        getTableData(workbookid, tbl.key)
            .then((newRows) => {
                this.setState({
                    rows: newRows,
                    loading: false
                });
            })
            .catch(catchEndpoint);
    };

    public handleClickDownload = () => {
        const { match } = this.props;
        const { workbookid } = match.params;
        getDownloadList(workbookid, false as boolean, store.currentTable.key).catch(catchEndpoint);
    };

    public render() {
        const { changes, rows, workbook, loading } = this.state;

        return (
            <>
                <Template
                    title={workbook && `${workbook.name || ""} (${workbook.year || ""})`}
                    wbId={workbook && `${workbook.id || ""}`}
                    locked={workbook && workbook.is_locked}
                >
                    {rows && rows.length > 0 && (
                        <FlexHorizontal>
                            {(store.editing && <div className="workbook_table_title">{store.currentTable.name}</div>) || (
                                <div className="workbook_blue">
                                    <Dropdown
                                        icon={ArrowDownIcon}
                                        iconRight
                                        alignLeft
                                        title={store.currentTable.name}
                                        items={outputTables as MenuItem[]}
                                        onClick={this.handleSelectWorkbook}
                                        menuWidth={400}
                                        data-at_id="workbook_blue"
                                    />
                                </div>
                            )}
                            <FlexSpacer />
                            {store.editing && (
                                <div
                                    style={{
                                        marginTop: "10px",
                                        marginBottom: "22px"
                                    }}
                                >
                                    {changes.length} {lang.numberChanges}
                                </div>
                            )}
                            <div className="container_action">
                                {!store.editing && <Button title={lang.download} onClick={this.handleClickDownload} data-at_id="download_table" />}
                                {(store.permission === "CONTROL" || store.permission === "WORK") &&
                                    workbook &&
                                    !workbook.is_locked &&
                                    store.editing && <Button title={lang.cancel} outlined onClick={this.onCancelEdit} />}
                                {(store.permission === "CONTROL" || store.permission === "WORK") && workbook && !workbook.is_locked && (
                                    <Button
                                        title={store.editing ? lang.save : lang.edit}
                                        onClick={this.onSaveEdit}
                                        onDisabledClick={this.onSaveEdit}
                                        loading={this.state.editSaving}
                                        disabled={this.state.editSaving}
                                        data-at_id={`button_${store.editing ? "save" : "edit"}`}
                                    />
                                )}
                                {
                                    <Button
                                        onClick={() => {
                                            const setFilters = this.state.setFilters;
                                            this.setState({
                                                setFilters: !setFilters
                                            });
                                        }}
                                        outlined={this.state.setFilters}
                                        Icon={FilterListOutlinedIcon}
                                    />
                                }
                            </div>
                        </FlexHorizontal>
                    )}
                    <div className="view_workbook_table">
                        <div className="workbook_table_holder" data-at_id="workbook_table_holder">
                            {loading && (
                                <div className="loader fullscreen">
                                    <MuiCircularProgress
                                        style={{
                                            margin: "10px 8px"
                                        }}
                                        size={36}
                                    />
                                </div>
                            )}
                            {!loading && rows && rows.length === 0 && (
                                <>
                                    <Title className="text_center">{lang.nothingToDisplay}</Title>
                                    <div style={{ textAlign: "center" }}>{lang.pleaseUpload}</div>
                                </>
                            )}
                            {!loading && rows && rows.length > 0 && (
                                <DataGrid
                                    ref={this.gridRef}
                                    localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                                    className="workbook_data_grid"
                                    rows={rows}
                                    columns={columnLookup[store.currentTable.key].map((col: TableColumn, i: number) => {
                                        const editable = store.editing ? col.editable : false;
                                        const filterable =
                                            col.key === "entity_code" || col.key === "entity_name" || col.key === "row_number" ? true : false;
                                        // Editable is set at the column (not table level)...
                                        return {
                                            field: col.key,
                                            headerName: col.name,
                                            width: 250,
                                            editable,
                                            filterable,
                                            frozen: i === 0,
                                            valueFormatter: this.formatValue,
                                            renderCell: this.renderCell
                                        };
                                    })}
                                    filterModel={this.state.filters}
                                    getCellClassName={() => "react-grid-Cell__value"}
                                    processRowUpdate={this.processRowUpdate}
                                    checkboxSelection={false}
                                    disableRowSelectionOnClick
                                    disableColumnFilter={!this.state.setFilters}
                                />
                            )}
                        </div>
                    </div>
                </Template>
            </>
        );
    }
}

const WorkbookPage = withRouter(WorkbookPageBase);
export { WorkbookPage };
